/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import Link from 'next/link';
import { Hit, SearchResponse } from '@algolia/client-search';
import { DotIcon, Table } from '@spa-cars/ui';
import { PaginationInfo, Tax } from '@spa-cars/models';
import { validateString } from 'avilatek-utils';
import { useLazyQuery } from '@apollo/client';
import { useAlgolia } from '../../hooks';
import { GET_TAXES_LIST } from '../../graphql/queries';

const buildTableData = (taxes: Array<Tax | Hit<Tax>> = []) =>
  taxes.map((tax) => ({
    _id: tax._id ?? (tax as any).id,
    name: tax?.name ?? 'N/A',
    value: tax?.value ? `${tax?.value}%` : 'N/A',
    status: (
      <p className="flex gap-[10px] items-center">
        <DotIcon
          className={` ${
            tax?.active ? 'text-primary-300' : ' text-danger-200'
          } w-[15px] h-[15px]`}
        />
        {tax?.active ? 'Activo' : 'Inactivo'}
      </p>
    ),
  }));

interface PaginationDataProps {
  items: Array<Tax>;
  count: number;
  pageInfo: PaginationInfo;
}

export default function TaxTable() {
  const searchTax = useAlgolia();
  const searchIndex = searchTax.initIndex('taxes');
  const [searchResult, setSearchResult] = React.useState<Hit<Tax>[]>(null);
  const [paginationData, setPaginationData] = React.useState<
    PaginationDataProps | SearchResponse<Tax>
  >(null);
  const [perPage, setPerPage] = React.useState(10);
  const [searchInput, setSearchInput] = React.useState(''); // guarda lo que el usuario ingresa en el input de busqueda
  const [loadingSearch, setLoadingSearch] = React.useState(false);
  const [loadTaxes, { data, loading }] = useLazyQuery<{
    taxPagination: {
      items: Array<Tax>;
      count: number;
      pageInfo: PaginationInfo;
    };
  }>(GET_TAXES_LIST);

  const queryVariables = {
    perPage: perPage ?? 10,
    sort: 'CREATEDAT_DESC',
  };

  const handleGoToPage = (value) => {
    if (validateString(searchInput)) {
      searchTaxes(searchInput, value - 1); // ir a la siguiente pagina de la busqueda de algolia
    } else {
      loadTaxes({
        variables: {
          page: value ?? 1,
          ...queryVariables,
        },
        fetchPolicy: 'cache-and-network',
      }); // ir a la siguiente pagina del query
    }
  };

  const columns = React.useMemo(
    () => [
      {
        Header: (
          <p className="flex justify-between items-center">
            NOMBRE
            {/* <MenuIcon className="w-5 h-5 text-neutral-100" /> */}
          </p>
        ),

        accessor: 'name',
      },
      {
        Header: (
          <p className="flex justify-between items-center">
            VALOR PORCENTUAL
            {/* <MenuIcon className="w-5 h-5 text-neutral-100" /> */}
          </p>
        ),
        accessor: 'value',
      },
      {
        Header: (
          <p className="flex justify-between items-center">
            ESTATUS
            {/* <MenuIcon className="w-5 h-5 text-neutral-100" /> */}
          </p>
        ),
        accessor: 'status',
      },
    ],
    []
  );

  const filterResults = React.useCallback(
    (results: SearchResponse<Tax>) => results?.hits ?? [],
    []
  );

  const searchTaxes = async (value: string, page_: number) => {
    try {
      if (!validateString(value) || value === '') {
        loadTaxes({
          variables: {
            page: 1,
            ...queryVariables,
          },
          fetchPolicy: 'cache-and-network',
        });
        setPaginationData(data?.taxPagination);
        setSearchResult([]);
        return;
      }
      setLoadingSearch(true);
      const result = await searchIndex.search<Tax>(value, {
        page: page_,
        hitsPerPage: perPage ?? 10,
      });
      setLoadingSearch(false);
      setPaginationData(result);
      const resultsFiltered = filterResults(result);
      setSearchResult(resultsFiltered);
    } catch (err) {
      console.log(err);
    }
  };

  const onChangeSearch = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();
      setSearchInput(e.target.value);
    },
    []
  );

  const dataMemo = React.useMemo(
    () =>
      searchResult?.length > 0
        ? buildTableData(searchResult)
        : buildTableData((paginationData as PaginationDataProps)?.items) ?? [],
    [paginationData, searchResult]
  );

  React.useEffect(() => {
    if (data) {
      setPaginationData(data?.taxPagination);
    }
  }, [data]);

  React.useEffect(() => {
    searchTaxes(searchInput, 0); // para algolia, la paginación empieza en 0
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, perPage]);

  return (
    <Table
      columns={columns}
      perPage={perPage}
      setPerPage={setPerPage}
      data={dataMemo}
      error={null}
      loading={loading || loadingSearch}
      pageCount={0}
      header="Impuestos"
      pageNumber={
        ((paginationData as SearchResponse<Tax>)?.page ??
          ((paginationData as PaginationDataProps)?.pageInfo?.currentPage ??
            1) - 1) + 1
      }
      addButtonText="Impuesto"
      handleGoToPage={handleGoToPage}
      results={
        (paginationData as SearchResponse<Tax>)?.nbHits ??
        (paginationData as PaginationDataProps)?.count
      }
      path="app/taxes"
      link={Link}
      onChangeSearch={onChangeSearch}
    />
  );
}
